/**
 * FFT Analyzer — Voxengo-SPAN-style real-time spectrum plugin.
 *
 * Registered as an effect-kind plugin so it drops into any bus's FX
 * chain exactly like Chorus / Limiter / etc. Audio passes through
 * unchanged: signal routes in → pass-through gain → out. The
 * spectrum analysis happens in parallel via a `ChannelSplitter` and
 * two `AnalyserNode`s (L + R), whose frequency-domain data the UI
 * polls from a RAF loop via `getFftSnapshot(analyzerId)` in
 * `audio/meters.ts`.
 *
 * Why not Tone.FFT directly? Tone wraps `AnalyserNode` but the
 * wrapper's `getValue()` returns a normalised magnitude array that
 * doesn't match SPAN's dB-based display. Using the raw AnalyserNode
 * (`getFloatFrequencyData` → already in dB, spec-defined) lets us
 * skip a magnitude→dB conversion and keeps the hot read-path
 * allocation-free.
 *
 * Param taxonomy: some params tune the DSP (fftSize, smoothing,
 * window), others are view-only (channelMode, slopeDbPerOct,
 * peakHold, dbRange) and only consumed by the spectrum UI. Both
 * kinds live in the plugin's `paramSchema` so they persist through
 * `BusEffect.params` — the spectrum component reads them from there
 * alongside the live data.
 *
 * Instance identity: every `createNode(ctx, preset)` call generates a
 * fresh `analyzerId` that the Plugin-tab UI uses to look up the
 * analysers via `getFftSnapshot(id)`. The handle exposes the id via
 * a non-standard `getAnalyzerId()` accessor so the bus code can
 * thread it into store metadata when the tab opens.
 */

import { registerEffect } from '@/audio/plugins/registry'
import type { EffectPlugin, PluginHandle } from '@/audio/plugins/types'
import { disposeFftMeter, ensureFftMeter, resizeFftMeter } from '@/audio/meters'
import { registerToneContext } from './registerContext'

/** Supported FFT window sizes (powers of two, Web Audio spec). */
const FFT_SIZES: ReadonlyArray<number> = [512, 1024, 2048, 4096, 8192]

/** Window-function amplitude-correction factors for display-side
 *  dB offsets. Tone.FFT doesn't expose a window selector — the
 *  underlying AnalyserNode always applies Blackman. We keep the
 *  enum so future plugins can layer their own FFT with a chosen
 *  window + the UI can read the correction at draw time. */
export const WINDOW_OPTIONS = [
  [0, 'Blackman (default)'],
  [1, 'Hann'],
  [2, 'Flat top'],
  [3, 'Rectangular'],
] as const

const CHANNEL_MODE_OPTIONS = [
  [0, 'Stereo'],
  [1, 'Mono (L+R)'],
  [2, 'Mid / Side'],
  [3, 'Left only'],
  [4, 'Right only'],
] as const

const DB_RANGE_OPTIONS = [
  [0, '-60 → 0 dB'],
  [1, '-90 → 0 dB'],
  [2, '-120 → 0 dB'],
] as const

const PLUGIN_ID = 'fft-analyzer'

let registered = false

/**
 * Non-standard accessor on FFT-analyzer `PluginHandle`s — lets the
 * spectrum UI ask the handle which analyzer-id to poll in
 * `meters.ts`. Keeps the default `PluginHandle` contract clean
 * while still giving the one consumer that needs the id a typed
 * path to it.
 *
 * Consumers narrow the handle with `hasAnalyzerId(handle)` before
 * reading; unrelated plugins don't expose the getter so the check
 * is the correct API boundary.
 */
export interface FftAnalyzerHandle extends PluginHandle {
  getAnalyzerId(): string
}

export function hasAnalyzerId(h: PluginHandle): h is FftAnalyzerHandle {
  return typeof (h as Partial<FftAnalyzerHandle>).getAnalyzerId === 'function'
}

/**
 * Register the FFT analyzer with the plugin registry. Idempotent —
 * call from the engine boot alongside `registerToneEffects()`.
 */
export function registerFftAnalyzer(): void {
  if (registered) return
  registered = true
  registerEffect(fftAnalyzerPlugin)
}

let nextInstance = 0
function mintAnalyzerId(): string {
  nextInstance += 1
  return `fft-${nextInstance}-${Math.random().toString(36).slice(2, 8)}`
}

export const fftAnalyzerPlugin: EffectPlugin = {
  id: PLUGIN_ID,
  label: 'FFT Analyzer',
  category: 'utility',
  defaultPreset: {
    fftSize: 2048,
    smoothing: 0.3,
    window: 0,
    channelMode: 0,
    slopeDbPerOct: 4.5,
    peakHold: 1,
    dbRange: 1,
  },
  paramSchema: [
    {
      id: 'fftSize',
      label: 'FFT size',
      kind: 'select',
      default: 2048,
      options: FFT_SIZES.map(n => [n, String(n)]),
      help: 'Larger sizes give more frequency resolution but slower time response.',
    },
    {
      id: 'smoothing',
      label: 'Smoothing',
      kind: 'slider',
      min: 0,
      max: 0.95,
      step: 0.01,
      default: 0.3,
      help: 'Exponential averaging on the frequency bins — higher values look steadier but react more slowly.',
    },
    {
      id: 'window',
      label: 'Window',
      kind: 'select',
      default: 0,
      options: WINDOW_OPTIONS.map(([v, label]) => [v, label]),
      help: 'Analysis window function. View-only: the Web Audio AnalyserNode uses Blackman internally; this setting is reserved for future plugins that let us pick.',
    },
    {
      id: 'channelMode',
      label: 'Channel mode',
      kind: 'select',
      default: 0,
      options: CHANNEL_MODE_OPTIONS.map(([v, label]) => [v, label]),
      help: 'What the spectrum view renders. Mid/Side is computed from L/R at draw time.',
    },
    {
      id: 'slopeDbPerOct',
      label: 'Slope',
      kind: 'slider',
      min: 0,
      max: 6,
      step: 0.5,
      default: 4.5,
      help: 'Pink-noise tilt applied at draw time. 4.5 dB/oct is the SPAN default — pink noise looks flat.',
    },
    {
      id: 'peakHold',
      label: 'Peak hold',
      kind: 'toggle',
      default: 1,
      help: 'Show a slowly-decaying peak-envelope trace under the live spectrum.',
    },
    {
      id: 'dbRange',
      label: 'dB range',
      kind: 'select',
      default: 1,
      options: DB_RANGE_OPTIONS.map(([v, label]) => [v, label]),
    },
  ],

  createNode(ctx, preset = {}): FftAnalyzerHandle {
    registerToneContext(ctx)

    const analyzerId = mintAnalyzerId()
    const fftSize = FFT_SIZES.includes(preset.fftSize ?? 2048)
      ? (preset.fftSize ?? 2048)
      : 2048
    const { left, right } = ensureFftMeter(ctx, analyzerId, fftSize)

    // Signal path: audio flows in → pass-through gain → out. A
    // splitter taps the input so the analysers see the signal
    // without perturbing the live graph.
    const input = ctx.createGain()
    const output = ctx.createGain()
    const splitter = ctx.createChannelSplitter(2)

    // Mirror single-channel sources to both analysers so mono input
    // still renders correctly in L/R overlay mode. `channelCount: 2`
    // + the up-mixer in ChannelSplitter handles this at the spec
    // level: if input is mono, both splitter outputs carry the same
    // signal.
    input.channelCount = 2
    input.channelCountMode = 'explicit'
    input.channelInterpretation = 'speakers'

    input.connect(output)
    input.connect(splitter)
    splitter.connect(left, 0)
    splitter.connect(right, 1)

    // Initial smoothing from preset. `smoothingTimeConstant` is an
    // EMA coefficient; 0 = no smoothing, 0.95 = very slow.
    left.smoothingTimeConstant = preset.smoothing ?? 0.3
    right.smoothingTimeConstant = preset.smoothing ?? 0.3

    return {
      input,
      output,
      setParam(name, value) {
        if (name === 'fftSize') {
          const next = FFT_SIZES.includes(value) ? value : 2048
          resizeFftMeter(analyzerId, next)
        } else if (name === 'smoothing') {
          const clamped = Math.max(0, Math.min(0.95, value))
          left.smoothingTimeConstant = clamped
          right.smoothingTimeConstant = clamped
        }
        // Other params (window / channelMode / slopeDbPerOct /
        // peakHold / dbRange) are view-only — the UI reads them
        // straight from `BusEffect.params`; no DSP update needed.
      },
      dispose() {
        try {
          input.disconnect()
          output.disconnect()
          splitter.disconnect()
        } catch {
          /* already disconnected */
        }
        disposeFftMeter(analyzerId)
      },
      getAnalyzerId() {
        return analyzerId
      },
    }
  },
}
